import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, PenTool, Box, FileText, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";

const cadServices = [
  {
    icon: Box,
    title: "3D-Modelle & Konstruktion",
    description:
      "Aus Skizzen, Fotos oder Bemaßungszeichnungen entstehen saubere 3D-Modelle, druckfertig oder als STEP/STL zum Weiterverwenden.",
    details: [
      "Modelle nach Skizze oder Bemaßung",
      "Anpassung vorhandener Modelle",
      "Druckfertige STL-Dateien",
      "Exportformat nach Wunsch (STEP, STL, OBJ)",
    ],
  },
  {
    icon: PenTool,
    title: "Technische Zeichnungen",
    description:
      "Technische Zeichnungen aus 3D-Modellen oder nach Vorgabe, für Werkstätten, Hersteller oder zur eigenen Dokumentation.",
    details: [
      "2D-Zeichnungen aus 3D-Modellen",
      "Maß- und Toleranzangaben",
      "Schnitte, Ansichten, Details",
      "PDF oder DXF-Export",
    ],
  },
  {
    icon: FileText,
    title: "Nach Absprache",
    description:
      "Kleinere CAD-Aufgaben freiberuflich und unkompliziert. Einfach anfragen, gemeinsam schauen, ob es passt.",
    details: [
      "Kleinere Projekte und Einzelaufgaben",
      "Faire Konditionen",
      "Schnelle Kommunikation",
      "Kein Mindestauftragsvolumen",
    ],
  },
];

const CADPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Navbar />

      {/* Page Header */}
      <header className="bg-[#1d1d1f] pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/40 text-[13px] hover:text-white/70 transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Startseite
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-[13px] font-medium text-white/40 uppercase tracking-widest mb-3">
              Freiberuflich
            </p>
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-5">
              CAD &<br />Technisches Zeichnen
            </h1>
            <p className="text-[17px] text-white/55 max-w-xl leading-relaxed">
              3D-Konstruktionen und technische Zeichnungen,
              unkompliziert, nach Absprache.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Services */}
      <section ref={ref} className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-px bg-[#e5e5ea]">
            {cadServices.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white p-8 hover:bg-[#f5f5f7] transition-colors duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-[#1d1d1f]/5 flex items-center justify-center mb-6">
                  <s.icon className="w-4 h-4 text-[#1d1d1f]/50" />
                </div>
                <h3 className="text-[20px] font-semibold text-[#1d1d1f] mb-3">{s.title}</h3>
                <p className="text-[14px] text-[#6e6e73] leading-relaxed mb-6">{s.description}</p>
                <ul className="space-y-2">
                  {s.details.map((d) => (
                    <li key={d} className="flex items-start gap-2.5 text-[13px] text-[#6e6e73]">
                      <CheckCircle className="w-3.5 h-3.5 text-[#1d1d1f]/30 mt-0.5 shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* Tools */}
      <section className="bg-[#f5f5f7] py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[13px] font-medium text-[#6e6e73] uppercase tracking-widest mb-6">
              Software & Tools
            </p>
            <div className="flex flex-wrap gap-3">
              {["Autodesk Inventor", "Fusion 360", "AutoCAD", "Blender", "PrusaSlicer", "Cura"].map((tool) => (
                <span
                  key={tool}
                  className="text-[13px] font-medium px-4 py-2 rounded-full bg-white border border-black/5 text-[#1d1d1f]/60"
                >
                  {tool}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section id="kontakt">
        <ContactSection />
      </section>

      <Footer />
    </div>
  );
};

export default CADPage;
